import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { jsPDF } from 'jspdf';
import {
  User,
  Clock,
  LogIn,
  LogOut,
  Wallet,
  Download,
  CheckCircle,
  XCircle,
  CalendarIcon,
  FileText,
  IndianRupee,
  Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('employeeUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const [todayRes, historyRes, salaryRes] = await Promise.all([
        axios.get(`${API}/attendance`, { params: { employee_id: user.id, date: today } }),
        axios.get(`${API}/attendance`, { params: { employee_id: user.id } }),
        axios.get(`${API}/salary-records`, { params: { employee_id: user.id } })
      ]);

      setTodayAttendance(todayRes.data?.[0] || null);
      setAttendanceHistory(historyRes.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      setSalaryRecords(salaryRes.data.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months.indexOf(b.month) - months.indexOf(a.month);
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      const response = await axios.post(`${API}/attendance/check-in`);
      toast.success(`Checked in at ${response.data.time}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error checking in');
    }
  };

  const handleCheckOut = async () => {
    try {
      const response = await axios.post(`${API}/attendance/check-out`);
      toast.success(`Checked out! Total: ${response.data.total_hours} hours`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error checking out');
    }
  };

  const generateSalarySlipPDF = (record) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(11, 17, 33); // brand-navy
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('ATHARVA FINANCE SOLUTIONS', 105, 18, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Salary Slip', 105, 28, { align: 'center' });
    
    doc.setTextColor(212, 175, 55); // brand-gold
    doc.setFontSize(10);
    doc.text(`${record.month} ${record.year}`, 105, 35, { align: 'center' });
    
    // Employee Details
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Details', 20, 55);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${record.employee_name}`, 20, 65);
    doc.text(`Employee ID: ${user?.id?.substring(0, 8).toUpperCase()}`, 20, 72);
    doc.text(`Department: Finance`, 120, 65);
    doc.text(`Pay Period: ${record.month} ${record.year}`, 120, 72);
    
    // Salary Breakdown
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 80, 190, 80);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Earnings', 20, 90);
    doc.text('Amount (₹)', 160, 90);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    let y = 100;
    doc.text('Basic Salary', 20, y);
    doc.text(record.base_salary.toLocaleString('en-IN'), 170, y, { align: 'right' });
    
    y += 8;
    doc.text('Bonus', 20, y);
    doc.text(record.bonus.toLocaleString('en-IN'), 170, y, { align: 'right' });
    
    // Deductions
    y += 15;
    doc.setFont('helvetica', 'bold');
    doc.text('Deductions', 20, y);
    
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.text('Total Deductions', 20, y);
    doc.text(record.deductions.toLocaleString('en-IN'), 170, y, { align: 'right' });
    
    // Net Salary
    y += 15;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, 190, y);
    
    y += 10;
    doc.setFillColor(11, 17, 33);
    doc.rect(15, y - 5, 180, 12, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Net Salary', 20, y + 3);
    doc.text(`₹ ${record.net_salary.toLocaleString('en-IN')}`, 170, y + 3, { align: 'right' });
    
    // Payment Status
    y += 25;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Payment Status: ${record.status === 'paid' ? 'PAID' : 'PENDING'}`, 20, y);
    if (record.paid_date) {
      doc.text(`Payment Date: ${record.paid_date}`, 120, y);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('This is a computer-generated document. No signature required.', 105, 270, { align: 'center' });
    doc.text('Atharva Finance Solutions | Kolhapur | Contact: 7410001036', 105, 277, { align: 'center' });
    
    // Save
    doc.save(`Salary_Slip_${record.month}_${record.year}_${record.employee_name.replace(/\s/g, '_')}.pdf`);
    toast.success('Salary slip downloaded!');
  };

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="employee-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Welcome, {user?.name}!</h1>
          <p className="text-slate-500 mt-1">Employee Dashboard</p>
        </div>
        <Badge className="bg-brand-gold/10 text-brand-navy border-brand-gold text-sm px-3 py-1">
          <User className="h-4 w-4 mr-2" />
          Employee
        </Badge>
      </div>

      {/* Today's Attendance Card */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-brand-navy to-brand-navy-light text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">Today's Attendance</h3>
              <p className="text-slate-300 text-sm">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
              {todayAttendance && (
                <div className="flex items-center gap-4 mt-3">
                  {todayAttendance.check_in && (
                    <div className="flex items-center gap-2">
                      <LogIn className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm">In: {todayAttendance.check_in}</span>
                    </div>
                  )}
                  {todayAttendance.check_out && (
                    <div className="flex items-center gap-2">
                      <LogOut className="h-4 w-4 text-amber-400" />
                      <span className="text-sm">Out: {todayAttendance.check_out}</span>
                    </div>
                  )}
                  {todayAttendance.total_hours > 0 && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-400" />
                      <span className="text-sm">{todayAttendance.total_hours} hours</span>
                    </div>
                  )}
                </div>
              )}
              {!todayAttendance && (
                <p className="text-amber-300 text-sm mt-2">You haven't checked in yet today</p>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCheckIn}
                data-testid="employee-check-in-btn"
                disabled={todayAttendance?.check_in}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Check In
              </Button>
              <Button
                onClick={handleCheckOut}
                data-testid="employee-check-out-btn"
                disabled={!todayAttendance?.check_in || todayAttendance?.check_out}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Check Out
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Present Days</p>
                <p className="text-2xl font-bold text-brand-navy">
                  {attendanceHistory.filter(a => a.status === 'present').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Hours (This Month)</p>
                <p className="text-2xl font-bold text-brand-navy">
                  {attendanceHistory
                    .filter(a => {
                      const d = new Date(a.date);
                      const now = new Date();
                      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                    })
                    .reduce((sum, a) => sum + (a.total_hours || 0), 0)
                    .toFixed(1)} hrs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-xl flex items-center justify-center">
                <Wallet className="h-6 w-6 text-brand-gold" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Salary Slips</p>
                <p className="text-2xl font-bold text-brand-navy">{salaryRecords.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Attendance History */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {attendanceHistory.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No attendance records yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {attendanceHistory.slice(0, 10).map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-brand-navy">{record.date}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        {record.check_in && <span>In: {record.check_in}</span>}
                        {record.check_out && <span>Out: {record.check_out}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        record.status === 'present'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }>
                        {record.status === 'present' ? 'Present' : 'Absent'}
                      </Badge>
                      {record.total_hours > 0 && (
                        <p className="text-xs text-slate-500 mt-1">{record.total_hours} hrs</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Salary Slips */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Salary Slips
            </CardTitle>
          </CardHeader>
          <CardContent>
            {salaryRecords.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No salary slips available</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {salaryRecords.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100"
                  >
                    <div>
                      <p className="font-semibold text-brand-navy">{record.month} {record.year}</p>
                      <p className="text-lg font-bold text-brand-gold mt-1">
                        {formatCurrency(record.net_salary)}
                      </p>
                      <Badge className={
                        record.status === 'paid'
                          ? 'bg-emerald-100 text-emerald-700 mt-2'
                          : 'bg-amber-100 text-amber-700 mt-2'
                      }>
                        {record.status === 'paid' ? 'Paid' : 'Pending'}
                      </Badge>
                    </div>
                    <Button
                      onClick={() => generateSalarySlipPDF(record)}
                      data-testid={`download-slip-${record.id}`}
                      variant="outline"
                      className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Company Info */}
      <Card className="border-0 shadow-sm bg-slate-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-brand-gold" />
            </div>
            <div>
              <p className="font-semibold text-brand-navy">Atharva Finance Solutions</p>
              <p className="text-sm text-slate-500">For any queries, contact HR: 7410001036</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeDashboard;
